// ABOUTME: Group-header row for ShDataGrid3 — a divider row with a sticky-left chevron, call-site-rendered label and count.
// ABOUTME: Rendered by ShGridBody when a virtual row is tagged `__isGroupHeader`. Transparent background, auto-measured height.

import { memo } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Text from "../text/Text";
import { Box, useTheme } from "@mui/material";
import { tokens } from "../../../theme";

/**
 * ShGridGroupHeader
 *
 * Occupies one virtual-row slot inside ShGridBody's body-inner. The outer div uses
 * the virtualizer's `rowStyle` (position / top / left / width / transform) but
 * intentionally has NO explicit height — it auto-sizes to content and feeds its
 * measured height back to the virtualizer through `measureRef`, so the slot size
 * always matches what's actually drawn regardless of the row-height mode.
 *
 * Visual contract:
 *   - Transparent background (inherits whatever the rail has behind it).
 *   - Big top padding (visual breathing room between previous group's last row and this header).
 *   - Tiny bottom padding (header sits tight against its own first row below).
 *   - Full-width sticky-left content block so the chevron + label + count stay
 *     pinned to the visible left edge during horizontal scroll.
 *
 * Props:
 *   label      — ReactNode from the call site's `grouping.renderLabel(key, sampleRow, count)`.
 *                The grid OWNS the layout (chevron + label + count); the call site only
 *                provides the styled label JSX (e.g. a <ShLabel/> matching the state's colors).
 *   count      — number of data rows in the bucket
 *   collapsed  — whether the group is currently collapsed (drives chevron rotation)
 *   onToggle   — () => void, called when the user clicks the header
 *   rowStyle   — inline style from the virtualizer (no height)
 *   measureRef — virtualizer.measureElement, attached to the outer div
 *   rowIndex   — virtual row index (needed by measureElement via data-index)
 *   colors     — theme color tokens (kept out of emotion so this row is cheap)
 *   isDark     — boolean, theme palette mode
 */
const ShGridGroupHeader = memo(function ShGridGroupHeader({
    label,
    count,
    collapsed,
    onToggle,
    rowStyle,
    measureRef,
    rowIndex,
    infiniteScroll
}) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    return (
        <div
            ref={measureRef}
            data-index={rowIndex}
            className="sh-grid-group-header"
            role="rowheader"
            aria-expanded={!collapsed}
            onClick={onToggle}
            style={{
                ...rowStyle,
                cursor: onToggle ? "pointer" : "default",
                minWidth: "100%",
                background: "transparent",
                userSelect: "none",
            }}
        >
            <Box
                sx={{
                    position: "sticky",
                    left: 0,
                    top:0,
                    display: "flex",
                    alignItems: "flex-end",
                    height: "100%",
                    borderBottom:`1px solid ${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`
                }}
            >
                <Box
                    sx={{
                        width:1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        paddingRight: 1.5,
                        paddingBottom: 1,
                        color: colors.txt['primary'],
                    }}
                >
                    
                    {label}
                    <Text light>
                        ({count})
                    </Text>
                    {!infiniteScroll &&
                        <KeyboardArrowDownIcon
                            fontSize="small"
                            sx={{
                                ml:"auto",
                                transition: "transform 150ms ease",
                                transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)",
                                color: colors.txt.secondary,
                            }}
                        />
                    }
                </Box>
            </Box>
        </div>
    );
});

export default ShGridGroupHeader;
